import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ApplicationState } from '../../store';
import {
  clearAbandonedCart,
  fetchAbandonedCart,
  setDismissed
} from '../../store/ducks/abandoned-cart';
import { AbandonedCartPartner } from '../../store/ducks/abandoned-cart/types';
import useIsBusinessTypeCG from '../../hooks/useIsBusinessTypeCG';
import useIsCommerceUser from '../../hooks/useIsCommerceUser';
import useAbandonedCartService from '../../services/MVP/useAbandonedCartService';
import { useCartService } from '../../services/MVP/useCartService';
import { updateLongSteelShoppingCartSettings } from '../../store/ducks/long-steel-shopping-cart/actions';
import { GetFlag } from '../../utils/featureFlag';
import { filterInvalidCustomers } from '../../utils/commerceUser';
import { Button } from '@gerdau/hefesto';
import {
  AbandonedCartAnchor,
  AbandonedCartCard,
  CardHeader,
  CardTitle,
  CloseButton,
  PartnerAddress,
  PartnerBlock,
  PartnerLabel,
  ProductBlock,
  ProductExtra,
  ProductImage,
  ProductInfo,
  ProductName
} from './styles';

const SESSION_TRIGGERED_KEY = 'abandoned_cart_triggered';
const SESSION_DISMISSED_KEY = 'abandoned_cart_dismissed';

const SESSION_NOTIFIED_KEY = 'abandoned_cart_notified';

const ELIGIBLE_ROUTES = [
  '/purchase/commerce/steel-type-choose',
  '/dashboard',
  '/orders/steel-type-choose',
  '/orders/long-steel',
  '/financials',
  '/download-area',
  '/favorites'
];

const CART_ROUTE = '/purchase/long-steel/commerce/shopping-cart';

const CARD_TITLE_ID = 'abandoned-cart-title';
const CARD_BADGE_ID = 'abandoned-cart-badge';

interface PartnerInfoProps {
  label: string;
  partner: AbandonedCartPartner;
}

const PartnerInfo: React.FC<PartnerInfoProps> = ({ label, partner }) => {
  const addressLine = [
    partner.address.street.join(', ').replace('[', '').replace(']', ''),
    partner.address.city,
    partner.address.region
  ]
    .filter(Boolean)
    .join(' - ');

  return (
    <PartnerBlock>
      <PartnerLabel>
        {label}: {partner.name}
      </PartnerLabel>
      <PartnerAddress>Endereço: {addressLine}</PartnerAddress>
    </PartnerBlock>
  );
};

const AbandonedCartReminder: React.FC = () => {
  const isEnabled = GetFlag('abandoned_cart');
  const isCommerceUser = useIsCommerceUser();
  const isCG = useIsBusinessTypeCG();

  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();

  const { notifyAbandonedCart } = useAbandonedCartService();
  const { createNewCart } = useCartService();

  const data = useSelector(
    (state: ApplicationState) => state.abandonedCart.data
  );
  const dismissed = useSelector(
    (state: ApplicationState) => state.abandonedCart.dismissed
  );

  const shoppingCartState = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );
  const hasActivePurchaseSelection = Boolean(
    shoppingCartState.emitterId || shoppingCartState.receiverId
  );

  const customers = useSelector(
    (state: ApplicationState) => state.customers.customers
  );
  const ordersInputCustomers = useSelector(
    (state: ApplicationState) => state.user.roles.ordersInputCustomers
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imgError, setImgError] = useState(false);

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );

  const notifiedRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const hasFetchedRef = useRef(false);

  const sk = useCallback(
    (base: string) => (data ? `${base}_${data.quoteId}` : base),
    [data]
  );

  const shouldShow = ELIGIBLE_ROUTES.some(
    (r) => pathname === r || pathname.startsWith(r + '/')
  );

  const isEligibleContext = isEnabled && isCommerceUser && !isCG;

  const cardVisible =
    isEligibleContext &&
    shouldShow &&
    data !== null &&
    !dismissed &&
    !hasActivePurchaseSelection &&
    !isMobile;

  const handleDismiss = useCallback(
    async (resumePurchase = false) => {
      if (!data || isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      setIsSubmitting(true);

      try {
        await notifyAbandonedCart(data.quoteId, {
          notify1: true,
          notify2: true
        });

        if (resumePurchase) {
          const resumeEmitterId = data.sold.bp;
          const resumeReceiverId = data.ship.bp || data.sold.bp;

          dispatch(
            updateLongSteelShoppingCartSettings({
              ...shoppingCartState,
              emitterId: resumeEmitterId,
              receiverId: resumeReceiverId
            })
          );
          await createNewCart(resumeEmitterId, resumeReceiverId, false);
        }
      } catch {
        //
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }

      sessionStorage.setItem(sk(SESSION_DISMISSED_KEY), 'true');
      dispatch(setDismissed());

      if (resumePurchase) {
        history.push(CART_ROUTE);
      }
    },
    [
      data,
      dispatch,
      history,
      notifyAbandonedCart,
      createNewCart,
      shoppingCartState,
      sk
    ]
  );

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const check = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth < 768), 100);
    };
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('resize', check);
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!isEligibleContext) return;
    if (customers.length === 0) return;
    if (sessionStorage.getItem(SESSION_TRIGGERED_KEY)) return;
    if (hasFetchedRef.current) return;

    const authorizedBps = filterInvalidCustomers(customers, true)
      .filter((customer) => ordersInputCustomers.includes(customer.customerId))
      .map((customer) => customer.customerId);

    if (authorizedBps.length === 0) return;

    hasFetchedRef.current = true;
    sessionStorage.setItem(SESSION_TRIGGERED_KEY, 'true');

    dispatch(fetchAbandonedCart(authorizedBps));
  }, [isEligibleContext, customers, ordersInputCustomers, dispatch]);

  useEffect(() => {
    if (!cardVisible || !data) return;
    if (notifiedRef.current || sessionStorage.getItem(sk(SESSION_NOTIFIED_KEY)))
      return;
    notifiedRef.current = true;
    sessionStorage.setItem(sk(SESSION_NOTIFIED_KEY), 'true');

    const { notify2 } = data.notification;
    notifyAbandonedCart(data.quoteId, { notify1: true, notify2 });
  }, [cardVisible, data, notifyAbandonedCart, sk]);

  useEffect(() => {
    if (!cardVisible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDismiss(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [cardVisible, handleDismiss]);

  useEffect(() => {
    setImgError(false);
  }, [data]);

  useEffect(() => {
    if (!isEligibleContext) {
      sessionStorage.removeItem(SESSION_TRIGGERED_KEY);
      sessionStorage.removeItem(sk(SESSION_DISMISSED_KEY));
      sessionStorage.removeItem(sk(SESSION_NOTIFIED_KEY));
      dispatch(clearAbandonedCart());
    }
  }, [isEligibleContext, dispatch, sk]);

  if (!isEligibleContext || isMobile || hasActivePurchaseSelection) return null;

  const soldCustomerType = customers.find(
    (customer) => customer.customerId === data?.sold.bp
  )?.customerType;
  const hasReceiver = data !== null && soldCustomerType === 'CC';
  const extraCount = data !== null ? data.quoteCount - 1 : 0;

  return (
    <>
      {cardVisible && data && (
        <AbandonedCartAnchor>
          <AbandonedCartCard
            role="region"
            aria-labelledby={CARD_TITLE_ID}
            aria-describedby={CARD_BADGE_ID}
          >
            <CardHeader>
              <CardTitle id={CARD_TITLE_ID}>
                Você tem itens no carrinho
              </CardTitle>
              <CloseButton
                type="button"
                onClick={() => handleDismiss(false)}
                aria-label="Fechar lembrete de carrinho"
                disabled={isSubmitting}
              >
                <span aria-hidden="true">✕</span>
              </CloseButton>
            </CardHeader>

            <PartnerInfo label="Emissor" partner={data.sold} />

            {hasReceiver && (
              <>
                <PartnerInfo label="Recebedor" partner={data.ship} />
              </>
            )}

            <ProductBlock>
              {data.firstItem.image && !imgError && (
                <ProductImage
                  src={data.firstItem.image}
                  alt={data.firstItem.name}
                  onError={() => setImgError(true)}
                />
              )}
              <ProductInfo>
                <ProductName>{data.firstItem.name}</ProductName>
                {extraCount > 0 ? (
                  <ProductExtra>
                    e outros {extraCount} itens continuam disponíveis no seu
                    carrinho
                  </ProductExtra>
                ) : (
                  <ProductExtra>
                    continua disponível no seu carrinho
                  </ProductExtra>
                )}
              </ProductInfo>
            </ProductBlock>

            <Button
              name="resume-purchase"
              type="button"
              onClick={() => handleDismiss(true)}
              disabled={isSubmitting}
              fullWidth
            >
              Retomar compra
            </Button>
          </AbandonedCartCard>
        </AbandonedCartAnchor>
      )}
    </>
  );
};

export default AbandonedCartReminder;
